/**
 * @fileoverview Shared error handling for VisualDX: logging and optional UI feedback.
 */

/** CSS class applied to elements in error state (stylesheet defines look). */
const ERROR_CLASS = 'vdx-error';

/** Id of the injected style tag so we only inject once. */
const STYLE_ID = 'vdx-errors-styles';

/** Hostname suffix for Webflow preview/published domains – visual errors only show here. */
const WEBFLOW_DOMAIN_SUFFIX = '.webflow.io';

/**
 * True when the current origin is a Webflow domain (*.webflow.io). Visual error state (showErrorOnElement) only runs on these domains.
 * @returns {boolean}
 */
function isWebflowDomain() {
    if (typeof window === 'undefined' || !window.location || !window.location.hostname) return false;
    return window.location.hostname.endsWith(WEBFLOW_DOMAIN_SUFFIX);
}

/** Default styles for .vdx-error – injected once so pages don't need their own CSS. */
const ERROR_STYLES = `.${ERROR_CLASS}{border:1px dotted #ef4444 !important;cursor:help;}`;

let stylesInjected = false;

/**
 * Injects global .vdx-error styles into the document so error state is visible without page CSS.
 */
function injectErrorStyles() {
    if (typeof document === 'undefined' || !isWebflowDomain()) return;
    if (document.getElementById(STYLE_ID)) {
        stylesInjected = true;
        return;
    }
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = ERROR_STYLES;
    (document.head || document.documentElement).appendChild(style);
    stylesInjected = true;
}

/**
 * Logs an error with optional module context.
 * @param {string} message
 * @param {{ module?: string, error?: unknown, context?: Record<string, unknown> }} [options]
 */
export function logError(message, options = {}) {
    const { module, error, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.error(prefix, message, error != null ? error : '', context ?? '');
}

/**
 * Logs a warning with optional module context.
 * @param {string} message
 * @param {{ module?: string, context?: Record<string, unknown> }} [options]
 */
export function logWarn(message, options = {}) {
    const { module, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.warn(prefix, message, context ?? '');
}

/**
 * Logs an info message with optional module context.
 * @param {string} message
 * @param {{ module?: string, context?: Record<string, unknown> }} [options]
 */
export function logInfo(message, options = {}) {
    const { module, context } = options;
    const prefix = module ? `[vdx/${module}]` : '[vdx]';
    console.log(prefix, message, context ?? '');
}

/**
 * Shows error state on an element (class + title/aria). Styling lives in CSS.
 * @param {Element} element
 * @param {string} message
 * @param {{ class: string } | void} [options]
 */
export function showErrorOnElement(element, message, options = {}) {
    if (!element || typeof element.classList === 'undefined') return;
    if (!isWebflowDomain()) return;
    if (!stylesInjected) injectErrorStyles();
    const errorClass = options.class ?? ERROR_CLASS;
    element.setAttribute('title', message);
    element.setAttribute('aria-invalid', 'true');
    element.classList.add(errorClass);
}

/**
 * Removes error state from an element.
 * @param {Element} element
 * @param {{ class: string } | void} [options]
 */
export function clearErrorOnElement(element, options = {}) {
    if (!element || typeof element.classList === 'undefined') return;
    const errorClass = options.class ?? ERROR_CLASS;
    element.removeAttribute('title');
    element.removeAttribute('aria-invalid');
    element.classList.remove(errorClass);
}

/**
 * Logs the error and optionally shows it on an element.
 * @param {string} message
 * @param {{
 *   module?: string;
 *   error?: unknown;
 *   element?: Element | null;
 *   showOnElement?: boolean;
 *   errorClass?: string;
 * }} [options]
 */
export function handleError(message, options = {}) {
    const { module = 'app', error, element, showOnElement, errorClass } = options;
    logError(message, { module, error });
    if (showOnElement && element) {
        showErrorOnElement(element, message, errorClass ? { class: errorClass } : undefined);
    }
}
